import { useEffect, useState } from 'react'
import { THEME_EVENT } from './theme'

/**
 * CHART THEME — the JS bridge to the palette in app/assets/css/theme.css.
 *
 * Charts are the one part of the UI that CSS cannot reach: Google Charts
 * renders SVG with its own inline styling, so it cannot read the --app-*
 * custom properties. Without this file it falls back to its light defaults —
 * a white plot box with near-black axis labels — which on the dark theme
 * reads as a broken panel.
 *
 * This used to be a frozen table of hex values duplicated from theme.css, with
 * a comment asking whoever changed a colour to change it here too. That stops
 * working the moment there are two palettes, so the values are now READ from
 * the live custom properties instead — theme.css stays the single source of
 * truth and there is nothing left to keep in sync.
 *
 * A chart.js counterpart lived here too, until the only chart.js chart (the
 * placeholder bar graph on Manage Payslips) was removed.
 */

const readVar = (styles, name, fallback) =>
  styles.getPropertyValue(name).trim() || fallback

/** Light-mode order, for the window before the stylesheet resolves. */
const CHART_SERIES_FALLBACK = ['#22292b', '#394447', '#4b585b', '#67787c', '#d0d6d8']

/**
 * Categorical series. chart-1..5 are five NEUTRALS, so a chart stays quiet
 * rather than competing with the red that means "primary" everywhere else.
 * Status colour still means something in a table cell; a pie slice separates
 * from its neighbour by lightness instead.
 *
 * The five are the same in both modes but their ORDER is not — theme.css
 * runs them dark-to-light on the white page and light-to-dark on the near
 * black one, so the lead series always separates from the ground it is drawn
 * on. That is why this reads them instead of hardcoding an order.
 */
const SERIES_VARS = ['--app-chart-1', '--app-chart-2', '--app-chart-3',
                     '--app-chart-4', '--app-chart-5']

/** Snapshot of the palette as it is painted right now. */
export const getChartTheme = () => {
  const styles = getComputedStyle(document.documentElement)
  return {
    surface: readVar(styles, '--app-surface', '#ffffff'),
    text: readVar(styles, '--app-text', '#090b0c'),
    muted: readVar(styles, '--app-muted', '#67787c'),
    label: readVar(styles, '--app-label', '#67787c'),
    // --app-border is translucent white on dark, which Google Charts will not
    // parse, so gridlines get their own opaque token.
    grid: readVar(styles, '--app-chart-grid', '#e3e7e8'),
    // Slice labels sit on a neutral fill whose lightness flips with the mode,
    // so the ink on them flips too.
    ink: readVar(styles, '--app-chart-ink', '#ffffff'),
    series: SERIES_VARS.map((v, i) => readVar(styles, v, CHART_SERIES_FALLBACK[i])),
  }
}

/**
 * Base options for every react-google-charts <Chart>. Spread first and let the
 * call site override: `options={{ ...chartOptions, title: '...' }}`.
 *
 * `backgroundColor: 'transparent'` is what stops the white plot box — the card
 * behind it shows through instead.
 */
export const getChartOptions = () => {
  const c = getChartTheme()
  return {
    backgroundColor: 'transparent',
    colors: c.series,
    fontName: 'inherit',
    titleTextStyle: { color: c.text, fontSize: 15, bold: true },
    legend: {
      position: 'bottom',
      alignment: 'center',
      textStyle: { color: c.muted, fontSize: 12 },
    },
    pieSliceTextStyle: { color: c.ink, fontSize: 12 },
    pieSliceBorderColor: c.surface,
    hAxis: {
      textStyle: { color: c.label, fontSize: 11 },
      titleTextStyle: { color: c.muted },
      gridlines: { color: c.grid },
      baselineColor: c.grid,
    },
    vAxis: {
      textStyle: { color: c.label, fontSize: 11 },
      titleTextStyle: { color: c.muted },
      gridlines: { color: c.grid },
      baselineColor: c.grid,
    },
    // Google renders tooltips as light chips it styles itself, so their text
    // is dark in both modes — this one does not follow --app-text.
    tooltip: { textStyle: { color: '#090b0c', fontSize: 12 } },
  }
}

/**
 * Re-reads the palette whenever the mode changes, so a chart re-colours with
 * the rest of the page instead of keeping the colours it booted with.
 */
export const useChartOptions = () => {
  const [options, setOptions] = useState(getChartOptions)

  useEffect(() => {
    const onThemeChange = () => setOptions(getChartOptions())
    window.addEventListener(THEME_EVENT, onThemeChange)
    return () => window.removeEventListener(THEME_EVENT, onThemeChange)
  }, [])

  return options
}
